import { useEffect, useRef } from "react";
import { useLocation } from "react-router-dom";

/**
 * Runs `callback(location)` on every navigation, but not on the initial render.
 *
 * This replaces react-router v5's `history.listen`, which v7 removed together
 * with `useHistory`. Skipping the first render is the whole point rather than a
 * detail: `history.listen` only ever fired on *subsequent* navigations, while a
 * plain `useEffect` on `location` also fires once on mount. Two of the three
 * call sites break outright if that extra call happens — the two nav hooks would
 * hard-reload the page through `window.location` during first paint, and App
 * would push a duplicate `virtualPageView` to GTM on every page load, doubling
 * every GA4 pageview.
 *
 * It also closes a leak the old code had: `history.listen` returns an
 * unsubscribe function that none of the three call sites ever called, and their
 * effects re-ran whenever the surrounding state changed, so listeners
 * accumulated over a session and each navigation ran the handler N times.
 */
const useLocationChange = (callback) => {
  const location = useLocation();
  const isFirstRender = useRef(true);

  // The callback lives in a ref so the effect can depend on the location alone.
  // Call sites pass inline arrows, which are new on every render — depending on
  // the callback would fire it on every render, and omitting it from the deps
  // without a ref would leave the effect holding a stale closure.
  const callbackRef = useRef(callback);
  callbackRef.current = callback;

  useEffect(() => {
    if (isFirstRender.current) {
      isFirstRender.current = false;
      return;
    }

    callbackRef.current(location);
  }, [location]);
};

export default useLocationChange;
